import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect } from 'react';
import TwoFactorAuthenticationQRCode from './TwoFactorAuthenticationQRCode/TwoFactorAuthenticationQRCode';
import TwoFactorAuthenticationInfo from './TwoFactorAuthenticationInfo/TwoFactorAuthenticationInfo';
import TwoFactorAuthenticationPin, { PinFormFeilds } from './TwoFactorAuthenticationPin/TwoFactorAuthenticationPin';
import usePostPortalGenerateQRCode from '../../../hooks/useApi/usePostPortalGenerateQRCode';
import usePostPortalAuthenticate from '../../../hooks/useApi/usePostPortalAuthenticate';
import { useClientLogin } from '../../../providers/ClientLogin.provider';
import { useTranslation } from 'react-i18next';

type TwoFactorAuthenticationContainerProps = {
  handleContinue: () => void;
  handleCancel: () => void;
  loginRequest: vp.ClientLogin;
};

type FC = FunctionComponent<TwoFactorAuthenticationContainerProps>;
enum TwoFactorAuthenticationState {
  PinCode,
  QRCode,
  TwoFactorInfomation,
}
const TwoFactorAuthenticationContainer: FC = (props: TwoFactorAuthenticationContainerProps) => {
  const { handleContinue, handleCancel, loginRequest } = props;
  const { response } = useClientLogin();
  const [twoFactorAuthenticationState, setTwoFactorAuthenticationState] = useState<TwoFactorAuthenticationState>(
    response.MFAEnabled ? TwoFactorAuthenticationState.PinCode : TwoFactorAuthenticationState.TwoFactorInfomation,
  );
  const [rememberDevice, setRememberDevice] = useState(false);
  const [postPortalGenerateQRCodeResponse, postPortalGenerateQRCode] = usePostPortalGenerateQRCode({});
  const [postPortalAuthenticateResponse, postPortalAuthenticate] = usePostPortalAuthenticate({});
  const [pinErrorMsg, setPinErrorMsg] = useState('');
  const [pinError, setPinErorr] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'Login.MFA' });

  useEffect(() => {
    if (postPortalGenerateQRCodeResponse) {
      setTwoFactorAuthenticationState(TwoFactorAuthenticationState.QRCode);
    }
  }, [postPortalGenerateQRCodeResponse]);
  useEffect(() => {
    if (postPortalAuthenticateResponse?.Success && postPortalAuthenticateResponse.Authorized) {
      if (rememberDevice) {
        localStorage.setItem('rememberDevice', 'true');
      }
      handleContinue();
    } else if (postPortalAuthenticateResponse) {
      setPinErorr(true);
      if (postPortalAuthenticateResponse?.Success) {
        setPinErrorMsg(t('invalidCodeValidation'));
      } else {
        setPinErrorMsg(postPortalAuthenticateResponse.ErrorMessage);
      }
    }
  }, [postPortalAuthenticateResponse]);
  const handlers = {
    continueTwoFactorInformation: () => {
      postPortalGenerateQRCode({
        Username: loginRequest.Username || '',
        PortalDomain: import.meta.env.VITE_DOMAIN,
      });
    },
    continueQRCode: () => {
      setTwoFactorAuthenticationState(TwoFactorAuthenticationState.PinCode);
    },
    submitPin: (values: PinFormFeilds) => {
      postPortalAuthenticate({
        Code: values.Code,
        Username: loginRequest.Username || '',
      });
      setRememberDevice(values.rememberDevice);
    },
  };
  return (
    <>
      {twoFactorAuthenticationState === TwoFactorAuthenticationState.TwoFactorInfomation && (
        <TwoFactorAuthenticationInfo handleContinue={handlers.continueTwoFactorInformation} />
      )}
      {twoFactorAuthenticationState === TwoFactorAuthenticationState.QRCode && (
        <TwoFactorAuthenticationQRCode
          QRCode={postPortalGenerateQRCodeResponse?.QRCode || ''}
          handleContinue={handlers.continueQRCode}
        />
      )}
      {twoFactorAuthenticationState === TwoFactorAuthenticationState.PinCode && (
        <TwoFactorAuthenticationPin
          handleCancel={handleCancel}
          handleFormSubmit={handlers.submitPin}
          initialValues={{ Code: '', rememberDevice: false }}
          hasError={pinError}
          errorMsg={pinErrorMsg}
        />
      )}
    </>
  );
};
export default TwoFactorAuthenticationContainer;
